// HistoryProbe — 히스토리 계측 오버레이 (B-1 복기⇄히스토리 무한 왕복 재현용, 2026-07-26)
//
// 왜 필요한가: 뒤로가기 구현(useBackTo·SwipeBack·nav(-1))은 코드상 정상인데 실기에서만
//   "복기 ⇄ 히스토리를 무한 왕복한다"는 증상이 난다. 네이티브는 HashRouter라
//   history.state.idx 신뢰성이 웹(BrowserRouter)과 다르고, iOS WKWebView는 앱 전환 시
//   state가 날아갈 수 있다. → 화면 전환마다 (idx / length / 전환 종류)를 눈으로 읽게 만든다.
//
// 게이트: VITE_HISTORY_DEBUG=1 로 빌드했을 때만 마운트된다(프로덕션 번들에는 트리셰이킹으로 미포함).
//   실기 검증 빌드:  VITE_HISTORY_DEBUG=1 npm run build
//
// 읽는 법: `#3/5 PUSH /review-history` = state.idx 3 · history.length 5 · PUSH 전환.
//   - 뒤로가기를 눌렀는데 idx가 안 줄면 → 뒤로가기가 실제로는 새 push다(왕복의 원인).
//   - idx가 length를 넘거나 0으로 리셋되면 → WebView가 state를 잃은 것.
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

export const HISTORY_PROBE_ON = import.meta.env.VITE_HISTORY_DEBUG === '1';

type Line = { seq: number; label: string };

// 외부(useBackTo·SwipeBack 등)에서 "무엇이 뒤로가기를 호출했는지"를 남기는 훅.
// 게이트 off면 완전한 no-op이라 호출부에 분기를 두지 않아도 된다.
let pushLine: ((label: string) => void) | null = null;
export function probeMark(label: string) {
  if (!HISTORY_PROBE_ON) return;
  pushLine?.(label);
}

function snap() {
  const idx = (window.history.state as { idx?: number } | null)?.idx;
  return `#${idx ?? '∅'}/${window.history.length}`;
}

export default function HistoryProbe() {
  const location = useLocation();
  const navType = useNavigationType(); // POP | PUSH | REPLACE
  const [lines, setLines] = useState<Line[]>([]);
  const seq = useRef(0);

  useEffect(() => {
    // setState 클로저가 최신 배열을 보도록 함수형 업데이트 + 최근 12줄만 유지
    pushLine = (label: string) => {
      seq.current += 1;
      const entry = { seq: seq.current, label };
      setLines((prev) => [...prev.slice(-11), entry]);
    };
    const onPop = () => pushLine?.(`popstate ${snap()}`);
    const onHash = () => pushLine?.(`hashchange ${snap()} ${location.hash || window.location.hash}`);
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onHash);
      pushLine = null;
    };
    // location.hash는 로그 문자열에만 쓰여 재구독이 불필요 — 마운트 1회 구독 유지
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 라우트가 바뀔 때마다 (전환종류 · idx/length · 경로) 한 줄
  useEffect(() => {
    pushLine?.(`${navType} ${snap()} ${location.pathname}${location.search}`);
  }, [location.key, navType, location.pathname, location.search]);

  return (
    <div
      style={{
        position: 'fixed', left: 4, right: 4, bottom: 4, zIndex: 99999,
        background: 'rgba(0,0,0,0.78)', color: '#7CFC9A', pointerEvents: 'none',
        font: '9px/1.25 ui-monospace, Menlo, monospace', padding: '4px 6px',
        borderRadius: 6, maxHeight: '34vh', overflow: 'hidden', whiteSpace: 'pre',
      }}
    >
      {lines.map((l) => `${String(l.seq).padStart(2, '0')} ${l.label}`).join('\n')}
    </div>
  );
}
